'use client';

import { Box, Flex, IconButton, Link, Text } from '@chakra-ui/react';
import { IconArrowUpRight, IconLayoutGrid, IconStar } from '@tabler/icons-react';
import type { MouseEvent, ReactNode } from 'react';

export type ExplorerCardDetail = { label: string; value?: string; highlighted?: boolean };

/** One compact card for modules, brands, and saved links. */
export function DataExplorerCard({
  href,
  title,
  subtitle,
  description,
  icon,
  details,
  favorite,
  favoritesReady = true,
  active,
  onNavigate,
  onToggleFavorite,
}: {
  href: string;
  title: string;
  subtitle?: string;
  description: string;
  icon?: ReactNode;
  details: ExplorerCardDetail[];
  favorite: boolean;
  favoritesReady?: boolean;
  active?: boolean;
  onNavigate: (event: MouseEvent<HTMLAnchorElement>) => void;
  onToggleFavorite: () => void;
}) {
  return (
    <Box
      as="article"
      className="explorer-card"
      position="relative"
      minW={0}
      borderWidth="1px"
      borderColor={active ? 'ui.border.strong' : 'ui.border.subtle'}
      borderRadius="8px"
      bg="ui.surface.card"
      overflow="hidden"
      _hover={{ borderColor: 'ui.border.strong', bg: 'ui.surface.rowHover' }}
      css={{
        '& .explorer-card-arrow': { opacity: 0, transition: 'opacity 120ms' },
        '&:hover .explorer-card-arrow, &:focus-within .explorer-card-arrow': { opacity: 1 },
        '@media (prefers-reduced-motion: reduce)': {
          '& .explorer-card-arrow': { transition: 'none' },
        },
      }}
    >
      <Link
        href={href}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        display="flex"
        flexDirection="column"
        alignItems="stretch"
        gap="0"
        h="full"
        color="ui.text.primary"
        textDecoration="none"
        _hover={{ textDecoration: 'none' }}
        _focusVisible={{
          outline: '2px solid',
          outlineColor: 'accent.focusRing',
          outlineOffset: '-2px',
        }}
      >
        <Box p="3.5" pb="3" flex="1">
          <Flex align="center" gap="2.5" pr="7" mb="2.5">
            <Flex
              boxSize="24px"
              flexShrink={0}
              align="center"
              justify="center"
              borderRadius="5px"
              bg="ui.surface.input"
              color="ui.text.secondary"
              fontSize="10px"
              fontWeight="700"
              aria-hidden="true"
              css={{ '& svg': { width: '17px', height: '17px' } }}
            >
              {icon ?? <IconLayoutGrid size={17} />}
            </Flex>
            <Box minW={0} flex="1">
              <Text
                fontSize="sm"
                fontWeight="600"
                lineHeight="1.3"
                letterSpacing="-0.015em"
                lineClamp={2}
              >
                {title}
              </Text>
              {subtitle || active ? (
                <Text
                  fontSize="10px"
                  color={active ? 'accent.fg' : 'ui.text.tertiary'}
                  mt="0.5"
                  truncate
                >
                  {active ? 'Current page' : subtitle}
                </Text>
              ) : null}
            </Box>
          </Flex>
          <Text fontSize="xs" lineHeight="1.5" color="ui.text.secondary" lineClamp={2}>
            {description}
          </Text>
        </Box>
        <Flex
          gap="2"
          px="3.5"
          py="2.5"
          align="center"
          bg="transparent"
          borderTopWidth="1px"
          borderColor="ui.border.subtle"
        >
          <Flex flex="1" minW={0} gap="3" align="center" wrap="wrap">
            {details.slice(0, 3).map(detail => (
              <Box key={detail.label} minW={0}>
                {detail.value ? (
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    lineHeight="1.2"
                    fontVariantNumeric="tabular-nums"
                    mb="0.5"
                  >
                    {detail.value}
                  </Text>
                ) : null}
                <Text
                  fontSize="10px"
                  lineHeight="1.3"
                  fontWeight={detail.highlighted ? '600' : '400'}
                  color={detail.highlighted ? 'accent.fg' : 'ui.text.tertiary'}
                >
                  {detail.label}
                </Text>
              </Box>
            ))}
          </Flex>
          <Box className="explorer-card-arrow" color="ui.text.tertiary" aria-hidden="true">
            <IconArrowUpRight size={15} />
          </Box>
        </Flex>
      </Link>
      <IconButton
        position="absolute"
        top="2.5"
        right="2"
        size="xs"
        variant="ghost"
        aria-label={`${favorite ? 'Remove' : 'Add'} ${title} ${favorite ? 'from' : 'to'} favorites`}
        aria-pressed={favorite}
        disabled={!favoritesReady}
        color={favorite ? 'ui.text.secondary' : 'ui.text.tertiary'}
        onClick={onToggleFavorite}
        _focusVisible={{
          outline: '2px solid',
          outlineColor: 'accent.focusRing',
          outlineOffset: '-2px',
        }}
      >
        <IconStar size={15} stroke={1.6} fill={favorite ? 'currentColor' : 'none'} />
      </IconButton>
    </Box>
  );
}
